import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import CancelIcon from "../../Atomos/Icons/Close";
import SaveChanges from "../../Atomos/Icons/EditMode/SaveChanges";
import {
  memberModalPrimaryButtonClassName,
  memberModalSecondaryButtonClassName,
} from "./memberModalFooterButtonClasses";
import InputTextArea from "../../staff/InputTextArea";
import InputText from "../../staff/InputText";
import { IPerson } from "../../../interfaces/models/IPerson";
import type { IPersonView } from "../../../interfaces/models/IPersonView";
import type { IPersonCreate } from "../../../interfaces/models/IPersonCreate";
import {
  ipersonToPersonCreate,
  mapPersonCreateToSavePayload,
} from "../../Organismos/Person/personForm/mapPerson";
import { updatePerson } from "../../../Services/Person";
import { toast } from "react-toastify";
import {
  AffiliationsEnum,
  type AffiliationsType,
} from "../../../Enums/PersonEnums";

const MODAL_PAD = "px-6 md:px-8";

type EditProfessionalCareerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: IPerson;
  onSaved: () => void;
};

const AFFILIATION_ORDER = Object.keys(AffiliationsEnum) as AffiliationsType[];
const ENUM_VALUE_SET = new Set<string>(Object.values(AffiliationsEnum));
const ADDITIONAL_AFFILIATION_MAX = 10;
const ADDITIONAL_AFFILIATION_SLOTS = 4 as const;

function normalizeIndustryOrganizationAffiliation(raw: unknown): string[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? [t] : [];
  }
  return [];
}

function parseAffiliationState(p: IPerson): {
  selected: AffiliationsType[];
  slots: [string, string, string, string];
} {
  const parts = normalizeIndustryOrganizationAffiliation(
    p.industryOrganizationAffiliation as unknown
  );
  const selected: AffiliationsType[] = [];
  const extras: string[] = [];
  for (const part of parts) {
    const t = part.trim();
    if (!t) {
      continue;
    }
    if (ENUM_VALUE_SET.has(t)) {
      const key = t as AffiliationsType;
      if (!selected.includes(key)) {
        selected.push(key);
      }
    } else {
      extras.push(t);
    }
  }
  const fromStored = p.additionalAffiliations ?? [];
  const slots: [string, string, string, string] = ["", "", "", ""];
  const merged = [...fromStored.map((x) => String(x).trim()), ...extras];
  for (let i = 0; i < ADDITIONAL_AFFILIATION_SLOTS; i++) {
    slots[i] = (merged[i] ?? "").slice(0, ADDITIONAL_AFFILIATION_MAX);
  }
  return { selected, slots };
}

function additionalActivitiesToString(p: IPerson): string {
  const a = p.additionalActivities?.trim() ?? "";
  const b = p.professionalActivitiesAndPreviousExperience?.trim() ?? "";
  if (a) {
    return a;
  }
  if (b) {
    return b;
  }
  return "";
}

const EditProfessionalCareerModal: React.FC<EditProfessionalCareerModalProps> = ({
  isOpen,
  onClose,
  person,
  onSaved,
}) => {
  const [significantAccomplishments, setSignificantAccomplishments] = useState("");
  const [selectedAffiliations, setSelectedAffiliations] = useState<AffiliationsType[]>([]);
  const [additionalAffiliationSlots, setAdditionalAffiliationSlots] = useState<
    [string, string, string, string]
  >(["", "", "", ""]);
  const [additionalActivities, setAdditionalActivities] = useState("");
  const [publications, setPublications] = useState("");
  const [seminarPresentation, setSeminarPresentation] = useState("");
  const [saving, setSaving] = useState(false);
  const draftInitKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      draftInitKeyRef.current = null;
      return;
    }
    const key = `${person.id ?? ""}|${person._ts ?? ""}`;
    if (draftInitKeyRef.current === key) {
      return;
    }
    draftInitKeyRef.current = key;
    setSignificantAccomplishments(person.significantAccomplishments?.trim() ?? "");
    const { selected, slots } = parseAffiliationState(person);
    setSelectedAffiliations(selected);
    setAdditionalAffiliationSlots(slots);
    setAdditionalActivities(additionalActivitiesToString(person));
    setPublications(person.publications?.trim() ?? "");
    setSeminarPresentation(person.seminarPresentation?.trim() ?? "");
  }, [isOpen, person]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const toggleAffiliation = useCallback((key: AffiliationsType) => {
    setSelectedAffiliations((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const setSlot = useCallback((index: number, value: string) => {
    const sliced = value.slice(0, ADDITIONAL_AFFILIATION_MAX);
    setAdditionalAffiliationSlots((prev) => {
      const next: [string, string, string, string] = [...prev];
      next[index] = sliced;
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!person.id?.trim()) {
      toast.error("Missing profile id.");
      return;
    }
    setSaving(true);
    try {
      const sortedEnum = [...selectedAffiliations].sort(
        (a, b) => AFFILIATION_ORDER.indexOf(a) - AFFILIATION_ORDER.indexOf(b)
      );
      const additionalFiltered = additionalAffiliationSlots
        .map((s) => s.trim())
        .filter(Boolean);

      const additionalActivitiesText = additionalActivities.trim();
      const form: IPersonCreate = {
        ...ipersonToPersonCreate(person),
        significantAccomplishments: significantAccomplishments.trim(),
        industryOrganizationAffiliation:
          sortedEnum.length > 0
            ? (sortedEnum as unknown as IPersonCreate["industryOrganizationAffiliation"])
            : ([] as unknown as IPersonCreate["industryOrganizationAffiliation"]),
        additionalAffiliations:
          additionalFiltered.length > 0 ? additionalFiltered : [],
        additionalActivities: additionalActivitiesText,
        professionalActivitiesAndPreviousExperience: additionalActivitiesText,
        publications: publications.trim(),
        seminarPresentation: seminarPresentation.trim(),
      };
      const payload = mapPersonCreateToSavePayload(form, {
        id: person.id,
        legacyId: (person as { legacyId?: string }).legacyId,
      } as IPersonView);
      await updatePerson(payload);
      toast.success("Professional career updated.");
      onSaved();
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        typeof data === "string" && data.trim()
          ? data.trim()
          : err instanceof Error
            ? err.message
            : "Could not save professional career.";
      toast.error(message);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const enricherHint = (
    <p className="mb-3 text-xs text-gray-500">
      Enriched text: you can paste basic HTML (e.g. paragraphs, lists, links). It will render on your
      profile when saved.
    </p>
  );

  const card = (
    <div
      className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="edit-professional-career-title"
    >
          <div
            className={`flex shrink-0 items-start justify-between gap-4 ${MODAL_PAD} pt-6 md:pt-8`}
          >
            <h2
              id="edit-professional-career-title"
              className="font-decimal text-2xl font-semibold text-primary"
            >
              Professional Career
            </h2>
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-gray-font hover:text-gray-600"
              onClick={onClose}
              disabled={saving}
              aria-label="Close"
            >
              <CloseModal />
            </button>
          </div>

          <div
            className={`min-h-0 flex-1 overflow-y-auto ${MODAL_PAD} pb-4 pt-6 md:pt-6`}
          >
            <div className="flex flex-col gap-8">
              <section>
                <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">
                  Significant Accomplishments
                </h3>
                {enricherHint}
                <InputTextArea
                  title=""
                  value={significantAccomplishments}
                  onChange={setSignificantAccomplishments}
                  placeholder="e.g. key matters, awards, publications — HTML optional"
                  maxChars={8000}
                  showCharacterCount
                  minHeightClassName="min-h-[160px]"
                />
              </section>

              <section>
                <h3 className="mb-3 font-decimal text-lg font-semibold text-primary">Affiliations</h3>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                  {AFFILIATION_ORDER.map((key) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 text-sm text-gray-font md:text-base"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAffiliations.includes(key)}
                        onChange={() => toggleAffiliation(key)}
                        disabled={saving}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{AffiliationsEnum[key]}</span>
                    </label>
                  ))}
                </div>
                <p className="mb-3 mt-5 text-xs text-gray-500">
                  Additional affiliations (optional, max {ADDITIONAL_AFFILIATION_MAX} characters each).
                </p>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {([0, 1, 2, 3] as const).map((i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <InputText
                        title=""
                        value={additionalAffiliationSlots[i]}
                        onChange={(v) => setSlot(i, v)}
                        disabled={saving}
                        inputClassName="!text-sm"
                      />
                      <span className="text-right text-xs text-gray-400">
                        {additionalAffiliationSlots[i].length} / {ADDITIONAL_AFFILIATION_MAX}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">Articles</h3>
                {enricherHint}
                <InputTextArea
                  title=""
                  value={publications}
                  onChange={setPublications}
                  placeholder="e.g. articles, publications, writing — HTML optional"
                  maxChars={2000}
                  showCharacterCount
                  minHeightClassName="min-h-[160px]"
                />
              </section>

              <section>
                <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">
                  Additional Activities
                </h3>
                {enricherHint}
                <InputTextArea
                  title=""
                  value={additionalActivities}
                  onChange={setAdditionalActivities}
                  placeholder="e.g. pro bono, boards, community work — HTML optional"
                  maxChars={2000}
                  showCharacterCount
                  minHeightClassName="min-h-[160px]"
                />
              </section>

              <section>
                <h3 className="mb-1 font-decimal text-lg font-semibold text-primary">
                  Seminar / Presentations
                </h3>
                {enricherHint}
                <InputTextArea
                  title=""
                  value={seminarPresentation}
                  onChange={setSeminarPresentation}
                  placeholder="e.g. speaking engagements, panels — HTML optional"
                  maxChars={2000}
                  showCharacterCount
                  minHeightClassName="min-h-[160px]"
                />
              </section>
            </div>
          </div>

          <div className={`flex flex-wrap justify-end gap-3 border-t border-gray-200 ${MODAL_PAD} py-5`}>
            <button
              type="button"
              className={memberModalSecondaryButtonClassName}
              onClick={onClose}
              disabled={saving}
            >
              <span>Cancel</span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font">
                <CancelIcon className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              className={memberModalPrimaryButtonClassName}
              onClick={() => void handleSave()}
              disabled={saving}
            >
              <span>Update Information</span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white [&_svg]:fill-current">
                <SaveChanges />
              </span>
            </button>
          </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onClick={saving ? undefined : onClose}
      role="presentation"
    >
      {card}
    </div>,
    document.body
  );
};

export default EditProfessionalCareerModal;
